import { useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Menu, X, ArrowRight, ArrowUpRight, PhoneCall, Sparkles } from "lucide-react";
import { EASE, PrimaryButton, GhostButton } from "@/components/ui-bits";
import {
  Problem,
  Services,
  HowItWorks,
  Benefits,
  Offer,
  Testimonials,
  Faq,
} from "@/components/Sections";
import DemoForm from "@/components/DemoForm";

const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Benefits", href: "#benefits" },
  { label: "FAQ", href: "#faq" },
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1727434032773-af3cd98375ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJsdWUlMjBnbG93aW5nJTIwbGluZXMlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc4NDA3MDAyNHww&ixlib=rb-4.1.0&q=85";

function Logo() {
  return (
    <a href="#top" data-testid="logo-link" className="flex items-center gap-2.5 group">
      <span className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-[#00F0FF] transition-transform duration-300 group-hover:rotate-6">
        <PhoneCall className="h-4.5 w-4.5 text-[#0C1120]" strokeWidth={2.5} />
      </span>
      <span className="font-heading text-lg font-bold tracking-tight text-white">
        Point Vision <span className="text-[#00F0FF]">AI</span>
      </span>
    </a>
  );
}

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: EASE }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled
          ? "border-b border-white/10 bg-[#0C1120]/80 backdrop-blur-xl"
          : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-18 max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <Logo />
        <nav className="hidden items-center gap-9 lg:flex">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-medium text-slate-300 transition-colors duration-200 hover:text-[#00F0FF]"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden lg:block">
          <PrimaryButton data-testid="nav-book-demo" onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}>
            Book a Free Demo <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </PrimaryButton>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          className="text-white lg:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="overflow-hidden border-t border-white/10 bg-[#0C1120]/95 backdrop-blur-xl lg:hidden"
        >
          <div className="flex flex-col gap-1 px-5 py-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-3 text-base font-medium text-slate-200 hover:bg-white/5"
              >
                {l.label}
              </a>
            ))}
            <PrimaryButton
              className="mt-2 w-full"
              onClick={() => {
                setOpen(false);
                document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Book a Free Demo <ArrowRight className="h-4 w-4" />
            </PrimaryButton>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}

const HERO_LINES = ["Never Miss", "Another Lead"];

function Hero() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 140]);
  const bgScale = useTransform(scrollY, [0, 700], [1, 1.12]);

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
  };
  const lineWrap = { hidden: {}, show: {} };
  const line = {
    hidden: { y: "110%" },
    show: { y: 0, transition: { duration: 1, ease: EASE } },
  };

  return (
    <section id="top" data-testid="hero" className="relative min-h-screen overflow-hidden pv-grain">
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0"
      >
        <img src={HERO_IMG} alt="" className="h-full w-full object-cover opacity-60" />
      </motion.div>
      <div className="absolute inset-0 z-0 bg-[#0C1120]/80" />
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-[#0C1120] via-[#0C1120]/40 to-[#0C1120]/70" />
      <div className="absolute -left-40 top-1/3 z-0 h-96 w-96 rounded-full bg-[#3A82FF]/20 blur-[120px]" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-5 pt-28 pb-16 sm:px-8">
        <motion.div variants={container} initial="hidden" animate="show" className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-200 backdrop-blur"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#00F0FF] pv-live-dot" />
            AI Employees for Service Businesses
          </motion.div>

          <h1 className="font-heading text-5xl font-bold leading-[0.98] tracking-tighter sm:text-7xl lg:text-8xl">
            {HERO_LINES.map((t, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  variants={line}
                  className={`block ${i === 1 ? "pv-cyan-gradient" : "pv-text-gradient"}`}
                >
                  {t}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            variants={line}
            className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-300 sm:text-xl"
          >
            Point Vision AI answers missed calls, follows up with leads, and books
            appointments for your business — <span className="text-white font-semibold">24/7</span>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
            className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <PrimaryButton
              data-testid="hero-book-demo"
              onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
            >
              Book a Free Demo <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </PrimaryButton>
            <GhostButton
              data-testid="hero-how-it-works"
              onClick={() => document.querySelector("#how-it-works")?.scrollIntoView({ behavior: "smooth" })}
            >
              See How It Works <ArrowUpRight className="h-4 w-4" />
            </GhostButton>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

const RIBBON = [
  "Missed-Call Text Back",
  "AI Receptionist",
  "Lead Qualification",
  "Appointment Booking",
  "Automated Follow-Up",
  "Review Requests",
  "CRM Organization",
  "24/7 Availability",
];

function Ribbon() {
  return (
    <div className="border-y border-white/10 bg-[#0C1120] py-5" data-testid="ribbon">
      <Marquee speed={40} gradient gradientColor="#0C1120" gradientWidth={120}>
        {RIBBON.map((t, i) => (
          <span key={i} className="mx-8 flex items-center gap-8 text-sm font-semibold uppercase tracking-[0.28em] text-slate-500">
            {t}
            <Sparkles className="h-3.5 w-3.5 text-[#00F0FF]/70" />
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function Footer() {
  return (
    <footer data-testid="footer" className="relative border-t border-white/10 bg-[#0C1120] pv-grain">
      <div className="relative z-10 mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="grid gap-12 md:grid-cols-[1.5fr_1fr_1fr]">
          <div>
            <Logo />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">
              Point Vision AI builds AI employees and AI receptionists that answer
              missed calls, follow up with leads, and book appointments for service
              businesses — around the clock.
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Explore</p>
            <ul className="mt-5 space-y-3 text-sm text-slate-400">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="transition-colors hover:text-[#00F0FF]">{l.label}</a>
                </li>
              ))}
              <li><a href="#contact" className="transition-colors hover:text-[#00F0FF]">Book a Free Demo</a></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Contact</p>
            <ul className="mt-5 space-y-3 text-sm text-slate-400">
              <li>
                <a href="mailto:nico@pointvisionco.com" data-testid="footer-email" className="transition-colors hover:text-[#00F0FF]">
                  nico@pointvisionco.com
                </a>
              </li>
              <li><a href="#contact" className="transition-colors hover:text-[#00F0FF]">Request a Demo</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-14 flex flex-col gap-4 border-t border-white/10 pt-8 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Point Vision AI. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="#" data-testid="footer-privacy" className="transition-colors hover:text-white">Privacy Policy</a>
            <a href="#" data-testid="footer-terms" className="transition-colors hover:text-white">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="bg-[#0C1120] text-white">
      <Navbar />
      <Hero />
      <Ribbon />
      <Problem />
      <Services />
      <HowItWorks />
      <Benefits />
      <Offer />
      <Testimonials />
      <Faq />
      <DemoForm />
      <Footer />
    </div>
  );
}
